import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { toolboxService } from '../services/api';

const MODE_MONTH = 'month';
const MODE_RANGE = 'range';

const CLID_OPTIONS = [
  { value: 'CLID3596', label: 'CLID3596 (OncoCare)' },
  { value: 'DEMO0004', label: 'DEMO0004 (testing)' },
];
const DEFAULT_CLID = 'CLID3596';

const EXAMPLE_CSV = `\uFEFFsubmitted_at,clid,customer_phone,conversation_id,opt_out,q1_nps,q2_criteria,q2_others,q3_booking,q3_registering_waiting,q3_doctor_consultation,q3_chemo_radiotherapy_treatment,q3_pharmacy_payment,q4_feedback,summary
2026-07-01T10:15:00+00:00,CLID3596,+85290000001,concierge::CLID3596::+85290000001,false,10,"Waiting time; Centre staff service attitude; Others",Friendly nurses,5,4,5,5,4,Great care overall,"[OncoCare Patient Survey Result]

Q1 NPS Score: 10
Q2 Criteria: Waiting time, Centre staff service attitude, Others: Friendly nurses
Q3 Process Satisfaction:
  - Booking Process: 5
  - Registering & Waiting: 4
  - Doctor Consultation: 5
  - Chemo/Radiotherapy/Treatment: 5
  - Pharmacy & Payment: 4
Q4 Feedback: Great care overall"
2026-07-02T08:30:00+00:00,CLID3596,+85290000002,concierge::CLID3596::+85290000002,false,7,,,,,,,"[OncoCare Patient Survey Result]

Q1 NPS Score: 7
Q2 Criteria: (skipped)
Q3 Process Satisfaction: (skipped)
Q4 Feedback: (skipped)"
2026-07-03T14:00:00+00:00,CLID3596,+85290000003,concierge::CLID3596::+85290000003,true,,,,,,,"[OncoCare Patient Survey Result]

Q1 NPS Score: N/A
Q2 Criteria: (skipped)
Q3 Process Satisfaction: (skipped)
Q4 Feedback: (skipped)"
`;

function SummaryChips({ summary }) {
  if (!summary) return null;
  const items = [
    { label: 'Total', value: summary.total },
    { label: 'Completed', value: summary.completed },
    { label: 'Opt-out', value: summary.opt_out_count },
    { label: 'Avg NPS', value: summary.avg_nps != null ? summary.avg_nps : '—' },
    { label: 'Promoters (9–10)', value: summary.promoter_count },
    { label: 'Non-promoters (0–8)', value: summary.non_promoter_count },
  ];
  return (
    <dl className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-sm">
      {items.map(({ label, value }) => (
        <div key={label} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
          <dt className="text-gray-500 text-xs">{label}</dt>
          <dd className="font-medium text-gray-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function PreviewTable({ rows, total }) {
  const columns = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return Object.keys(rows[0]);
  }, [rows]);

  if (!rows || rows.length === 0) {
    return <p className="mt-4 text-sm text-gray-500">No survey rows in this range.</p>;
  }

  return (
    <div className="mt-4 overflow-x-auto">
      <p className="text-sm text-gray-500 mb-2">
        Showing {rows.length} of {total}
      </p>
      <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="px-3 py-2 text-left font-medium text-gray-600 border-b border-gray-200 whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/80'}>
              {columns.map((col) => (
                <td
                  key={col}
                  className="px-3 py-2 border-b border-gray-100 text-gray-800 max-w-xs truncate"
                  title={row[col] != null ? String(row[col]) : ''}
                >
                  {row[col] == null ? '' : String(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const OncoCareSurveyReport = () => {
  const [mode, setMode] = useState(MODE_MONTH);
  const [clid, setClid] = useState(DEFAULT_CLID);
  const [yearMonth, setYearMonth] = useState(dayjs().format('YYYY-MM'));
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().add(1, 'month').startOf('month').format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const isRangeValid = startDate && endDate && startDate < endDate;
  const canRun = mode === MODE_MONTH ? Boolean(yearMonth) : isRangeValid;

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    const res = await toolboxService.previewOncoCareSurveyReport({
      mode,
      yearMonth,
      startDate,
      endDate,
      previewLimit: 500,
      clid,
    });
    setLoading(false);
    if (!res.success) {
      setError(res.error || 'Preview failed');
      return;
    }
    setPreview(res.data);
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    const res = await toolboxService.exportOncoCareSurveyReportCsv({
      mode,
      yearMonth,
      startDate,
      endDate,
      clid,
    });
    setExporting(false);
    if (!res.success) {
      setError(res.error || 'Export failed');
      return;
    }
    const tag =
      mode === MODE_RANGE
        ? `${startDate.replace(/-/g, '')}_${endDate.replace(/-/g, '')}`
        : yearMonth.replace(/-/g, '');
    const url = URL.createObjectURL(res.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oncocare_survey_${clid}_${tag}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadExample = () => {
    const blob = new Blob([EXAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'oncocare_survey_export_example.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-4">
        <Link to="/toolbox" className="text-sm text-[#075e54] hover:underline">
          Back to Tool Box
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">OncoCare Survey Report</h1>
        <p className="text-sm text-gray-600 mb-6">
          Preview and export OncoCare patient NPS survey results for a month or custom date range.
          Default clinic is CLID3596; use DEMO0004 for testing.
        </p>

        <div className="mb-4 flex flex-wrap gap-4 items-center">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="radio"
              name="oncocare-mode"
              value={MODE_MONTH}
              checked={mode === MODE_MONTH}
              onChange={() => setMode(MODE_MONTH)}
            />
            By month
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="radio"
              name="oncocare-mode"
              value={MODE_RANGE}
              checked={mode === MODE_RANGE}
              onChange={() => setMode(MODE_RANGE)}
            />
            Custom date range
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="oncocare-clid" className="block text-xs font-medium text-gray-500 mb-1">
              Clinic (CLID)
            </label>
            <select
              id="oncocare-clid"
              value={clid}
              onChange={(e) => setClid(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            >
              {CLID_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {mode === MODE_MONTH ? (
            <div>
              <label htmlFor="oncocare-month" className="block text-xs font-medium text-gray-500 mb-1">
                Month
              </label>
              <input
                id="oncocare-month"
                type="month"
                value={yearMonth}
                onChange={(e) => setYearMonth(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="oncocare-start-date" className="block text-xs font-medium text-gray-500 mb-1">
                  Start date (inclusive)
                </label>
                <input
                  id="oncocare-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="oncocare-end-date" className="block text-xs font-medium text-gray-500 mb-1">
                  End date (exclusive)
                </label>
                <input
                  id="oncocare-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              {startDate && endDate && !isRangeValid && (
                <p className="text-xs text-red-500 self-end pb-2">End date must be after start date.</p>
              )}
            </>
          )}
          <button
            type="button"
            onClick={handlePreview}
            disabled={loading || !canRun}
            className="px-4 py-2 rounded-md bg-[#075e54] text-white text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Run preview'}
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !canRun}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-800 disabled:opacity-50"
          >
            {exporting ? 'Preparing CSV…' : 'Download CSV'}
          </button>
          <button
            type="button"
            onClick={handleDownloadExample}
            className="px-4 py-2 rounded-md border border-dashed border-gray-300 text-sm font-medium text-gray-600"
          >
            Download example CSV
          </button>
        </div>

        {error ? (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}

        {preview && (
          <div className="mt-6">
            <p className="text-sm text-gray-700">
              <span className="font-medium">CLID:</span> {preview.clid || '—'}
            </p>
            <p className="text-sm text-gray-700 mt-1">
              <span className="font-medium">Range:</span> {preview.start_ts} → {preview.end_ts}
            </p>
            <SummaryChips summary={preview.summary} />
            <PreviewTable rows={preview.rows} total={preview.total} />
          </div>
        )}
      </div>
    </div>
  );
};

export default OncoCareSurveyReport;
